import { Button, HStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Compass, RefreshDouble, WifiOff, WarningCircle } from 'iconoir-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { recoverFromDynamicImportLoadError } from '@/libs/dynamic-import-recovery';
import { ErrorLayout } from './ui/ErrorLayout';

function getErrorStatus(error: unknown): number | null {
  if (isRouteErrorResponse(error)) return error.status;
  if (error instanceof Response) return error.status;
  return null;
}

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const status = getErrorStatus(error);
  const isNotFound = status === 404;
  const isServiceUnavailable = status === 503;

  useEffect(() => {
    if (recoverFromDynamicImportLoadError(error)) {
      return;
    }

    // Report unexpected errors to Sentry (not 404s or connection issues)
    if (!isNotFound && !isServiceUnavailable) {
      Sentry.captureException(error, {
        tags: { 'error.route_boundary': true },
      });
    }
  }, [error, isNotFound, isServiceUnavailable]);

  if (isNotFound) {
    return (
      <ErrorLayout
        icon={
          <Compass width={36} height={36} color="var(--chakra-colors-gray-500)" strokeWidth={1.5} />
        }
        code={t('routeError.notFound.code')}
        title={t('routeError.notFound.title')}
        description={t('routeError.notFound.description')}
        accentColor="gray"
      >
        <Button
          colorPalette="gray"
          variant="solid"
          size="lg"
          onClick={() => navigate('/dashboard')}
        >
          {t('routeError.notFound.action')}
        </Button>
      </ErrorLayout>
    );
  }

  if (isServiceUnavailable) {
    return (
      <ErrorLayout
        icon={
          <WifiOff
            width={36}
            height={36}
            color="var(--chakra-colors-orange-500)"
            strokeWidth={1.5}
          />
        }
        title={t('routeError.connection.title')}
        description={t('routeError.connection.description')}
        accentColor="orange"
      >
        <Button colorPalette="gray" variant="solid" size="lg" onClick={() => navigate(0)}>
          <HStack gap={2}>
            <RefreshDouble width={18} height={18} />
            <span>{t('common.retry')}</span>
          </HStack>
        </Button>
      </ErrorLayout>
    );
  }

  return (
    <ErrorLayout
      icon={
        <WarningCircle
          width={36}
          height={36}
          color="var(--chakra-colors-red-500)"
          strokeWidth={1.5}
        />
      }
      title={t('routeError.generic.title')}
      description={t('routeError.generic.description')}
      accentColor="red"
    >
      <HStack gap={3}>
        <Button
          colorPalette="gray"
          variant="solid"
          size="lg"
          onClick={() => navigate('/dashboard')}
        >
          {t('routeError.generic.goHome')}
        </Button>
        <Button variant="outline" size="lg" onClick={() => navigate(0)}>
          <HStack gap={2}>
            <RefreshDouble width={18} height={18} />
            <span>{t('common.retry')}</span>
          </HStack>
        </Button>
      </HStack>
    </ErrorLayout>
  );
}
